import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { OverlayModule } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { ToolingDef } from 'src/app/core/dto';
import { ToolingService } from '../../services/tooling.service';
import { UnitsService } from '../../services/units.service';

/** One column of the machine table inside the preview. */
interface MachineColumn {
  field: string;
  displayName: string;
  /** Inches on the wire; shown in the user's units. */
  isLength?: boolean;
  /** Logical on the wire; shown as a check mark. */
  isBool?: boolean;
}

/**
 * Hover preview for a tooling code, mirroring the material (coil type) preview: the
 * tooling's own values on top, then the per-machine tools behind it. Wrap the link that
 * shows the code with this component.
 */
@Component({
  selector: 'app-tooling-preview',
  templateUrl: './tooling-preview.component.html',
  styleUrls: ['./tooling-preview.component.scss'],
  imports: [
    RouterModule,
    OverlayModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule
],
})
export class ToolingPreviewComponent implements OnInit, OnDestroy {
  @Input() toolingCode: string = '';
  tooling: ToolingDef;
  loadFailed = false;
  mainWidth = 1000;
  mainHeight = 300;
  loadingWidth = 500;
  bodyWidth = 0;
  bodyHeight = 0;
  isOpen = false;
  isMouseOnMain = false;
  isMouseOnContent = false;
  offsetX = 0;
  offsetY = 0;
  loadingPos = {
    top: '0px',
    left: '0px',
  };

  private machineColumns: MachineColumn[] = [
    { field: 'machineName', displayName: 'machine' },
    { field: 'description', displayName: 'description' },
    { field: 'finWidth', displayName: 'finWidth', isLength: true },
    { field: 'legHeight', displayName: 'legHeight', isLength: true },
    { field: 'legHeight2', displayName: 'legHeight2', isLength: true },
    { field: 'pCodeGroup', displayName: 'pCodeGroup' },
    { field: 'holeCount', displayName: 'holeCount' },
    { field: 'holeSpace', displayName: 'holeSpace', isLength: true },
    { field: 'stageBay', displayName: 'stageBay' },
    { field: 'loadDock', displayName: 'loadDock' },
    { field: 'calcLength', displayName: 'calcLength', isBool: true },
  ];

  /** The columns the tooling normalizes away are dropped; what's left is what differs. */
  shownMachineColumns: MachineColumn[] = [];

  timeoutId;

  private toolingService = inject(ToolingService);
  private unitsService = inject(UnitsService);

  onGetData() {
    this.toolingService.getTooling(this.toolingCode).subscribe({
      next: (tooling) => {
        this.tooling = tooling;
        this.shownMachineColumns = this.machineColumns.filter((column) => {
          switch (column.field) {
            case 'description':
              return tooling.needNormalizing?.descriptions;
            case 'pCodeGroup':
              return tooling.needNormalizing?.pCodeGroups;
            case 'finWidth':
              return tooling.needNormalizing?.finWidths;
            case 'legHeight':
              return tooling.needNormalizing?.legHeights;
            case 'legHeight2':
              return tooling.needNormalizing?.legHeights2;
            default:
              return true;
          }
        });
        this.sizeToContent(tooling.machines?.length ?? 0);
      },
      error: () => {
        // Nothing to preview - leave the trigger behaving like a plain link.
        this.loadFailed = true;
        this.isOpen = false;
      },
    });
  }

  /** The overlay is positioned from mainHeight, so it has to track the machine count. */
  private sizeToContent(machineCount: number) {
    const height = 260 + machineCount * 40;
    this.mainHeight = Math.min(600, Math.max(300, height));
  }

  /** Tooling-level lengths arrive comma-joined when the machines disagree; format each part. */
  toolingLength(value: string | number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return `${value}`
      .split(',')
      .map((part) => {
        const inches = Number(part.trim());
        return isNaN(inches)
          ? part.trim()
          : this.unitsService.formatUserUnits(inches, 'in', 3, false, '', false);
      })
      .join(', ');
  }

  isChecked(machine: any, column: MachineColumn): boolean {
    return !!machine?.[column.field];
  }

  machineText(machine: any, column: MachineColumn): string {
    const value = machine?.[column.field];
    if (value === null || value === undefined) {
      return '';
    }
    if (column.isLength) {
      return this.unitsService.formatUserUnits(value, 'in', 3, false, '', false);
    }
    return `${value}`;
  }

  onShowTooltip(e) {
    let pos = e.target.getBoundingClientRect();
    if (!this.toolingCode || this.loadFailed) {
      return;
    }
    this.timeoutId = setTimeout(() => {
      this.isMouseOnMain = true;
      this.onGetPosition(pos);
      if (!this.tooling) {
        setTimeout(() => {
          this.onGetData();
        }, 300);
      }
    }, 200);
  }

  onHideTooltip() {
    clearTimeout(this.timeoutId);
    if (this.isMouseOnMain) {
      this.isMouseOnMain = false;
      setTimeout(() => {
        if (!this.isMouseOnContent) {
          this.isOpen = false;
        }
      });
    }
  }

  onShowTooltip1() {
    this.isMouseOnContent = true;
  }

  onHideTooltip1() {
    this.isMouseOnContent = false;
    setTimeout(() => {
      if (!this.isMouseOnContent && !this.isMouseOnMain) {
        this.isOpen = false;
      }
    });
  }

  onGetPosition(pos) {
    let realTop = 0;
    let realRight = 0;
    const { top, left, right } = pos;
    const width = right - left;

    // Right-Down
    if (right < this.bodyWidth / 3 && top < this.bodyHeight / 3) {
      realRight = 0;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: '0px' };
    } // Center-Down
    else if (
      right < (this.bodyWidth * 2) / 3 &&
      right >= this.bodyWidth / 3 &&
      top < this.bodyHeight / 3
    ) {
      realRight = width / 2 - this.mainWidth / 2;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: `${this.mainWidth / 2 - this.loadingWidth / 2}px` };
    } // Left-Down
    else if (right >= (this.bodyWidth * 2) / 3 && top < this.bodyHeight / 3) {
      realRight = 0 - this.mainWidth + width;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: `${this.mainWidth - this.loadingWidth}px` };
    } // Right-Middle
    else if (
      right < this.bodyWidth / 2 &&
      top >= this.bodyHeight / 3 &&
      top < (this.bodyHeight * 2) / 3
    ) {
      realRight = right - left - 10;
      realTop = 0 - this.mainHeight / 2 - 10;
      this.loadingPos = { top: `${this.mainHeight / 2 - 20}px`, left: '0px' };
    } // Left-Middle
    else if (
      right >= this.bodyWidth / 2 &&
      top >= this.bodyHeight / 3 &&
      top < (this.bodyHeight * 2) / 3
    ) {
      realRight = 0 - this.mainWidth;
      realTop = 0 - this.mainHeight / 2 - 10;
      this.loadingPos = {
        top: `${this.mainHeight / 2 - 20}px`,
        left: `${this.mainWidth - this.loadingWidth}px`,
      };
    } // Right-Up
    else if (right < this.bodyWidth / 3 && top >= (this.bodyHeight * 2) / 3) {
      realRight = 0;
      realTop = 0 - this.mainHeight - 25;
      this.loadingPos = { top: `${this.mainHeight - 40}px`, left: '0px' };
    } // Center-Up
    else if (
      right < (this.bodyWidth * 2) / 3 &&
      right >= this.bodyWidth / 3 &&
      top >= (this.bodyHeight * 2) / 3
    ) {
      realRight = width / 2 - this.mainWidth / 2;
      realTop = 0 - this.mainHeight - 10;
      this.loadingPos = {
        top: `${this.mainHeight - 50}px`,
        left: `${this.mainWidth / 2 - this.loadingWidth / 2}px`,
      };
    } // Left-Up
    else {
      realRight = 0 - this.mainWidth + width;
      realTop = 0 - this.mainHeight - 10;
      this.loadingPos = {
        top: `${this.mainHeight - 40}px`,
        left: `${this.mainWidth - this.loadingWidth}px`,
      };
    }
    this.offsetX = realRight;
    this.offsetY = realTop;
    this.isOpen = true;
  }

  trackByKey = (index: number): number => {
    return index;
  };

  ngOnInit(): void {
    this.bodyWidth = window.innerWidth;
    this.bodyHeight = window.innerHeight;
  }

  ngOnDestroy() {
    clearTimeout(this.timeoutId);
    this.isOpen = false;
  }
}
